import React, { useState, useEffect } from 'react';
import { Plus, X, Trash2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAXONOMY_FIELDS = [
  { key: 'types', label: 'Types', description: 'Store types (e.g., retail, food, service)' },
  { key: 'categories', label: 'Categories', description: 'Store categories (e.g., fashion, lifestyle, sports)' },
  { key: 'segments', label: 'Segments', description: 'Store segments (e.g., premium, outlet, standard)' }
];

export const TaxonomyManager = ({ clientId }) => {
  const [taxonomy, setTaxonomy] = useState({ types: [], categories: [], segments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Add value state
  const [addingTo, setAddingTo] = useState(null); // which field is being added to
  const [newValue, setNewValue] = useState('');
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  
  // Delete state
  const [deleteError, setDeleteError] = useState(null);
  const [deletingValue, setDeletingValue] = useState(null);

  useEffect(() => {
    fetchTaxonomy();
  }, [clientId]);

  const fetchTaxonomy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/admin/unified-clients/${clientId}/taxonomy`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch taxonomy');
      }
      
      const data = await response.json();
      setTaxonomy(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddValue = async (field) => {
    if (!newValue.trim()) {
      setAddError('Value cannot be empty');
      return;
    }
    
    try {
      setAddLoading(true);
      setAddError(null);
      
      const response = await fetch(
        `${API_URL}/api/admin/unified-clients/${clientId}/taxonomy/${field}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: newValue.trim() })
        }
      );
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to add value');
      }
      
      // Success - refresh and reset
      await fetchTaxonomy();
      setNewValue('');
      setAddingTo(null);
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteValue = async (field, value) => {
    setDeletingValue(`${field}-${value}`);
    setDeleteError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/admin/unified-clients/${clientId}/taxonomy/${field}/${encodeURIComponent(value)}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        // Success - refresh
        await fetchTaxonomy();
        return;
      }
      
      // Try to parse error response
      let errorMessage = 'Failed to delete value';
      try {
        const data = await response.json();
        errorMessage = data.detail || errorMessage;
      } catch {
        // If response body was already consumed or invalid, check status
        if (response.status === 409) {
          errorMessage = `Cannot delete '${value}' - it is currently in use by one or more stores`;
        }
      }
      
      setDeleteError(errorMessage);
    } catch (err) {
      // Network or other errors
      setDeleteError(err.message || 'Failed to delete value');
    } finally {
      setDeletingValue(null);
    }
  };

  const startAdding = (field) => {
    setAddingTo(field);
    setNewValue('');
    setAddError(null);
  };

  const cancelAdding = () => {
    setAddingTo(null);
    setNewValue('');
    setAddError(null);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        Loading taxonomy...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="text-red-500 text-center">{error}</div>
        <button
          onClick={fetchTaxonomy}
          className="mt-4 mx-auto block px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="taxonomy-manager">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-medium text-gray-900">Taxonomy Management</h3>
        <p className="text-sm text-gray-500 mt-1">
          Manage the allowed values for store type, category, and segment fields.
          Values are stored in lowercase for consistency.
        </p>
      </div>

      {/* Delete Error Banner */}
      {deleteError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="delete-error">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{deleteError}</p>
          </div>
          <button
            onClick={() => setDeleteError(null)}
            className="text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Taxonomy Fields */}
      {TAXONOMY_FIELDS.map(({ key, label, description }) => (
        <div key={key} className="bg-white rounded-lg shadow" data-testid={`taxonomy-section-${key}`}>
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">{label}</h4>
                <p className="text-sm text-gray-500">{description}</p>
              </div>
              <button
                onClick={() => startAdding(key)}
                disabled={addingTo === key}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid={`add-${key}-button`}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </button>
            </div>
          </div>

          {/* Add Form */}
          {addingTo === key && (
            <div className="p-4 bg-blue-50 border-b border-blue-100" data-testid={`add-${key}-form`}>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddValue(key)}
                  placeholder={`Enter new ${label.toLowerCase().slice(0, -1)}...`}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid={`input-new-${key}`}
                  autoFocus
                />
                <button
                  onClick={() => handleAddValue(key)}
                  disabled={addLoading || !newValue.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`submit-add-${key}`}
                >
                  {addLoading ? 'Adding...' : 'Add'}
                </button>
                <button
                  onClick={cancelAdding}
                  disabled={addLoading}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                  data-testid={`cancel-add-${key}`}
                >
                  Cancel
                </button>
              </div>
              {addError && (
                <p className="mt-2 text-sm text-red-600" data-testid={`add-${key}-error`}>{addError}</p>
              )}
            </div>
          )}

          {/* Values List */}
          <div className="p-4">
            {taxonomy[key]?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {taxonomy[key].map((value) => (
                  <div
                    key={value}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full text-sm text-gray-700 group"
                    data-testid={`${key}-value-${value}`}
                  >
                    <span>{value}</span>
                    <button
                      onClick={() => handleDeleteValue(key, value)}
                      disabled={deletingValue === `${key}-${value}`}
                      className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                      title="Delete value"
                      data-testid={`delete-${key}-${value}`}
                    >
                      {deletingValue === `${key}-${value}` ? (
                        <span className="text-xs">...</span>
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No values defined yet</p>
            )}
          </div>
        </div>
      ))}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Note:</p>
            <p>Taxonomy values that are currently in use by one or more stores cannot be deleted. Remove the value from all stores first, then delete it from the taxonomy.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxonomyManager;
